import { Box } from '@factorialco/deprecated-design-system'

import i18n from 'modules/core/lib/i18n'

import { useCurrentIncidence } from 'modules/bookkeepers/lib/CurrentIncidenceContext'
import type IncidenceChanges from 'modules/bookkeepers/store/collections/changes'
import type IncidenceDetail from 'modules/bookkeepers/store/models/IncidenceDetail'
import useFetchIncidenceChangeItems from 'modules/payroll_bundle/hooks/useFetchIncidenceChangeItems'
import type { IncidenceChangeItem, IncidenceDetailsField } from 'modules/payroll_bundle/lib/types'
import ChangeItem from 'modules/payroll_bundle/pages/IncidenceDetailPage/_ChangedItem'
import ContractDetails from 'modules/payroll_bundle/pages/IncidenceDetailPage/_ContractInfo/ContractDetails'
import {
  BasicFields,
  ExtraFields,
  HoursFields,
  NewDetail,
  OldDetail,
  SalaryFields,
  getOldIncidenceDetail,
  hasAnyFieldUpdate,
} from 'modules/payroll_bundle/pages/IncidenceDetailPage/_ContractInfo/helper'

interface Props {
  incidenceDetailModel: IncidenceDetail
  incidenceChangesCollection: IncidenceChanges
  isBookkeeperView: boolean
}

const ContractInfo = ({
  incidenceDetailModel,
  incidenceChangesCollection,
  isBookkeeperView = false,
}: Props) => {
  const newIncidenceDetail: NewDetail = incidenceDetailModel.toJS()
  const oldIncidenceDetail: OldDetail = getOldIncidenceDetail(incidenceChangesCollection)
  const incidence = useCurrentIncidence()
  const { loading, changeItems } = useFetchIncidenceChangeItems(
    incidence.id,
    isBookkeeperView,
    incidence?.name ?? null,
    incidence?.targetId ?? null,
    incidence
  )

  const showContractInfoSection = (contractFields: IncidenceDetailsField[]) =>
    hasAnyFieldUpdate(oldIncidenceDetail, newIncidenceDetail, contractFields)

  return (
    <Box>
      {showContractInfoSection(BasicFields) && (
        <ContractDetails
          contractFields={BasicFields}
          oldIncidenceDetail={oldIncidenceDetail}
          newIncidenceDetail={newIncidenceDetail}
          title={i18n.t(
            'bookkeepers.incidence.details.sections.contract_basic_information_updated'
          )}
        />
      )}
      {showContractInfoSection(ExtraFields) && (
        <ContractDetails
          contractFields={ExtraFields}
          oldIncidenceDetail={oldIncidenceDetail}
          newIncidenceDetail={newIncidenceDetail}
          title={i18n.t(
            'bookkeepers.incidence.details.sections.contract_extra_information_updated'
          )}
        />
      )}
      {showContractInfoSection(HoursFields) && (
        <ContractDetails
          contractFields={HoursFields}
          oldIncidenceDetail={oldIncidenceDetail}
          newIncidenceDetail={newIncidenceDetail}
          title={i18n.t('bookkeepers.incidence.details.sections.contract_hours_updated')}
        />
      )}
      {showContractInfoSection(SalaryFields) && (
        <ContractDetails
          contractFields={SalaryFields}
          oldIncidenceDetail={oldIncidenceDetail}
          newIncidenceDetail={newIncidenceDetail}
          title={i18n.t('bookkeepers.incidence.details.sections.gross_salary_updated')}
          legalEntityId={incidence.legalEntityId}
        />
      )}
      {!loading ? (
        <Box gap="s2">
          {changeItems.reverse().map((change: IncidenceChangeItem, index: number) => {
            return <ChangeItem key={`${index}-${change.id}`} change={change} />
          })}
        </Box>
      ) : null}
    </Box>
  )
}

export default ContractInfo
